import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Select from "react-select";
import AddBoxIcon from "@mui/icons-material/AddBox";
import { AddElementsButton } from "../../../../Components/AddElementsButton";
import { Plus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { errorAlert, handleSuccess } from "../../../../Components/Alerts";
import { ApiLink } from "../../../../APILink/ApiLink";
import authHeader from "../../../../Auth/authHeader";
import PriceField from "../../../../Components/NumberFieldFormater";
import UnitModal from "../../../UnitCRUD/UnitModal";
import ArticleGroupModal from "../../../ArticleGroupsCRUD/ArticleGroupModal";
import { createUnit } from "../../../UnitCRUD/UnitService";
import { createArticleGroup } from "../../../ArticleGroupsCRUD/ArticleGroupService";
import {
  createCompoundArticle,
  fetchCompoundArticleById,
  getCompoundArticleErrorMessage,
  updateCompoundArticle,
} from "./CompoundArticleService";
import "./CompoundArticleFormModal.scss";

const articleProduction = [
  { id: 2, type: "Gjysëm Produkt" },
  { id: 3, type: "Produkt i Prodhuar" },
];

const saleTypes = [
  { id: 1, name: "Pakicë" },
  { id: 2, name: "Shumicë" },
];

const defaultForm = {
  Barcode: "",
  ArticleName: "",
  Description: "",
  ArticleProduction: 3,
  GroupID: "",
  UnitID: "",
  LedgerID: "",
  VAT: "",
  SalesPrice: "",
  IsActive: true,
  TouchScreen: false,
  ArticleOtherDetails: [
    {
      NewBarcode: "",
      ArticleUnitID: "",
      TypeOfSales: 1,
      ArticleUnitQty: 1,
      NewSalesPrice: "",
      IsActive: true,
    },
  ],
};

function toNumberOrZero(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function CompoundArticleModal({ open, onClose, onSuccess, itemToEdit, readOnly = false }) {
  const headers = authHeader();
  const isEditMode = Boolean(itemToEdit);
  const isReadOnly = Boolean(readOnly);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const [groups, setGroups] = useState([]);
  const [units, setUnits] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [vatRates, setVatRates] = useState([]);

  const [formData, setFormData] = useState(defaultForm);
  const [salesNoVat, setSalesNoVat] = useState(0);
  const [showAdvancedForm, setShowAdvancedForm] = useState(false);

  const [errors, setErrors] = useState({});
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);

  const [initialIdentity, setInitialIdentity] = useState({
    Barcode: "",
    ArticleName: "",
  });

  const vatPercent = useMemo(() => {
    const selected = vatRates.find((v) => Number(v.VATID) === Number(formData.VAT));
    return selected ? Number(selected.VATPercent || 0) : 0;
  }, [vatRates, formData.VAT]);

  const currentArticleId = useMemo(() => {
    if (!itemToEdit) return null;
    if (typeof itemToEdit === "number") return itemToEdit;
    return itemToEdit.ArticleID || itemToEdit.ArticleId || null;
  }, [itemToEdit]);

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 55,
      borderColor: state.selectProps.hasError ? "#d32f2f" : provided.borderColor,
      boxShadow: state.isFocused
        ? "0 0 0 3px rgba(78, 123, 103, 0.18)"
        : "none",
      ":hover": {
        borderColor: state.selectProps.hasError ? "#d32f2f" : "#4e7b67",
      },
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  const setField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setIsDirty(true);
  };

  const validateField = (field, value) => {
    const required = {
      ArticleName: "Emri i artikullit është i detyrueshëm.",
      Barcode: "Barkodi është i detyrueshëm.",
      GroupID: "Zgjedh grupin.",
      UnitID: "Zgjedh njësinë matëse.",
      LedgerID: "Zgjedh llogarinë kontabël.",
      VAT: "Zgjedh normën e TVSH-së.",
      ArticleProduction: "Zgjedh llojin e artikullit.",
    };
    if (required[field] && !value && value !== 0) {
      setErrors((prev) => ({ ...prev, [field]: required[field] }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const loadDependencies = async () => {
    const [groupRes, unitRes, ledgerRes, vatRes] = await Promise.all([
      axios.get(ApiLink.API_URL + "ArticleGroup", { headers }),
      axios.get(ApiLink.API_URL + "ArticleUnits/pick", { headers }),
      axios.get(ApiLink.API_URL + "Ledger/gettwoledgers", { headers }),
      axios.get(ApiLink.API_URL + "vatrates", { headers }),
    ]);

    setGroups(groupRes.data || []);
    setUnits(unitRes.data || []);
    setLedgers(ledgerRes.data || []);
    setVatRates(vatRes.data || []);
  };

  const loadInsertDefaults = async () => {
    const barcodeResponse = await axios.get(ApiLink.API_URL + "CompoundArticle/Barcode", {
      headers,
    });

    const nextBarcode = barcodeResponse?.data?.NextBarcode || "";

    setFormData({
      ...defaultForm,
      Barcode: nextBarcode,
      ArticleOtherDetails: [
        {
          NewBarcode: nextBarcode,
          ArticleUnitID: "",
          TypeOfSales: 1,
          ArticleUnitQty: 1,
          NewSalesPrice: "",
          IsActive: true,
        },
      ],
    });

    setInitialIdentity({
      Barcode: nextBarcode,
      ArticleName: "",
    });

    setSalesNoVat(0);
    setErrors({});
    setIsDirty(false);
  };

  const loadEditData = async () => {
    if (!currentArticleId) {
      throw new Error("Nuk u gjet ID e artikullit për editim.");
    }

    const data = await fetchCompoundArticleById(currentArticleId);

    setFormData({
      Barcode: data.Barcode || "",
      ArticleName: data.ArticleName || "",
      Description: data.Description || "",
      ArticleProduction: data.ArticleProduction || 3,
      GroupID: data.GroupID || "",
      UnitID: data.UnitID || "",
      LedgerID: data.LedgerID || "",
      VAT: data.VAT || "",
      SalesPrice: data.SalesPrice || 0,
      IsActive: data.IsActive ?? true,
      TouchScreen: data.TouchScreen ?? false,
      ArticleOtherDetails: (data.ArticleOtherDetails && data.ArticleOtherDetails.length > 0)
        ? data.ArticleOtherDetails.map((d) => ({
            ArticleSalesOptionsID: d.ArticleSalesOptionsID || null,
            NewBarcode: d.NewBarcode || "",
            ArticleUnitID: d.ArticleUnitID || "",
            TypeOfSales: d.TypeOfSales || 1,
            ArticleUnitQty: d.ArticleUnitQty || 1,
            NewSalesPrice: d.NewSalesPrice || 0,
            IsActive: d.IsActive ?? true,
          }))
        : [
            {
              ArticleSalesOptionsID: null,
              NewBarcode: data.Barcode || "",
              ArticleUnitID: data.UnitID || "",
              TypeOfSales: 1,
              ArticleUnitQty: 1,
              NewSalesPrice: data.SalesPrice || 0,
              IsActive: data.IsActive ?? true,
            },
          ],
    });

    setInitialIdentity({
      Barcode: data.Barcode || "",
      ArticleName: data.ArticleName || "",
    });

    const vatValue = Number(data.VATPercent || 0);
    const salesWithVat = Number(data.SalesPrice || 0);
    const basePrice = vatValue > 0 ? (salesWithVat * 100) / (100 + vatValue) : salesWithVat;
    setSalesNoVat(basePrice);
    setErrors({});
    setIsDirty(false);
    setShowAdvancedForm((data.ArticleOtherDetails || []).length > 1);
  };

  useEffect(() => {
    if (!open) return;

    const initialize = async () => {
      setLoading(true);
      try {
        await loadDependencies();
        if (isEditMode) {
          await loadEditData();
        } else {
          await loadInsertDefaults();
        }
      } catch (err) {
        errorAlert(getCompoundArticleErrorMessage(err, "Gabim gjatë ngarkimit të formës."));
        onClose();
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [open, isEditMode]);

  useEffect(() => {
    if (!vatPercent) {
      setSalesNoVat(toNumberOrZero(formData.SalesPrice));
      return;
    }

    const salesWithVat = toNumberOrZero(formData.SalesPrice);
    const withoutVat = (salesWithVat * 100) / (100 + vatPercent);
    setSalesNoVat(withoutVat);
  }, [formData.SalesPrice, vatPercent]);

  const validate = async () => {
    const nextErrors = {};

    if (!formData.Barcode?.trim()) nextErrors.Barcode = "Barkodi është i detyrueshëm.";
    if (!formData.ArticleName?.trim()) nextErrors.ArticleName = "Emri i artikullit është i detyrueshëm.";
    if (!formData.ArticleProduction) nextErrors.ArticleProduction = "Zgjedh llojin e artikullit.";
    if (!formData.GroupID) nextErrors.GroupID = "Zgjedh grupin.";
    if (!formData.UnitID) nextErrors.UnitID = "Zgjedh njësinë matëse.";
    if (!formData.LedgerID) nextErrors.LedgerID = "Zgjedh llogarinë kontabël.";
    if (!formData.VAT) nextErrors.VAT = "Zgjedh normën e TVSH-së.";

    const salesPrice = toNumberOrZero(formData.SalesPrice);
    if (salesPrice < 0) nextErrors.SalesPrice = "Çmimi i shitjes nuk mund të jetë negativ.";

    if (formData.Barcode?.trim() && formData.Barcode !== initialIdentity.Barcode) {
      const barcodeCheck = await axios.get(ApiLink.API_URL + "CompoundArticle/checkBarcode", {
        headers,
        params: { barcode: formData.Barcode.trim() },
      });
      const barcodeCount = Number(barcodeCheck?.data?.[0]?.Barcode || 0);
      if (barcodeCount > 0) nextErrors.Barcode = "Ky barkod ekziston.";
    }

    if (formData.ArticleName?.trim() && formData.ArticleName !== initialIdentity.ArticleName) {
      const nameCheck = await axios.get(ApiLink.API_URL + "CompoundArticle/chectArticleName", {
        headers,
        params: { articleName: formData.ArticleName.trim() },
      });
      const nameCount = Number(nameCheck?.data?.[0]?.ArticleName || 0);
      if (nameCount > 0) nextErrors.ArticleName = "Ky emër artikulli ekziston.";
    }

    if (showAdvancedForm) {
      const details = formData.ArticleOtherDetails || [];
      if (!details.length) {
        nextErrors.Advanced = "Shto të paktën një rresht në seksionin e avancuar.";
      }

      details.forEach((detail, index) => {
        if (!detail.TypeOfSales) {
          nextErrors[`AdvancedType_${index}`] = "Lloji i shitjes është i detyrueshëm.";
        }
        if (!detail.ArticleUnitID) {
          nextErrors[`AdvancedUnit_${index}`] = "Njësia është e detyrueshme.";
        }
        if (!detail.NewBarcode?.trim()) {
          nextErrors[`AdvancedBarcode_${index}`] = "Barkodi është i detyrueshëm.";
        }
        if (Number(detail.TypeOfSales) === 2 && toNumberOrZero(detail.ArticleUnitQty) <= 0) {
          nextErrors[`AdvancedQty_${index}`] = "Sasia e shumicës duhet të jetë më e madhe se 0.";
        }
        if (toNumberOrZero(detail.NewSalesPrice) < 0) {
          nextErrors[`AdvancedPrice_${index}`] = "Çmimi nuk mund të jetë negativ.";
        }
      });
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSalesNoVatChange = (value) => {
    const cleanValue = value === "" ? "" : Number(value);
    if (cleanValue === "") {
      setSalesNoVat(0);
      setField("SalesPrice", "");
      return;
    }

    setSalesNoVat(cleanValue);
    const withVat = vatPercent > 0 ? cleanValue * (1 + vatPercent / 100) : cleanValue;
    setField("SalesPrice", withVat.toFixed(2));

    if (!showAdvancedForm) {
      setFormData((prev) => ({
        ...prev,
        ArticleOtherDetails: [
          {
            ...(prev.ArticleOtherDetails?.[0] || {}),
            NewBarcode: prev.Barcode,
            ArticleUnitID: prev.UnitID,
            TypeOfSales: prev.ArticleOtherDetails?.[0]?.TypeOfSales || 1,
            ArticleUnitQty: prev.ArticleOtherDetails?.[0]?.TypeOfSales === 2
              ? (prev.ArticleOtherDetails?.[0]?.ArticleUnitQty || 1)
              : 1,
            NewSalesPrice: withVat.toFixed(2),
            IsActive: true,
          },
        ],
      }));
    }
  };

  const syncDefaultAdvancedRow = (partial) => {
    if (showAdvancedForm) return;

    setFormData((prev) => ({
      ...prev,
      ArticleOtherDetails: [
        {
          ...(prev.ArticleOtherDetails?.[0] || {}),
          NewBarcode: partial.NewBarcode ?? prev.Barcode,
          ArticleUnitID: partial.ArticleUnitID ?? prev.UnitID,
          TypeOfSales: partial.TypeOfSales ?? prev.ArticleOtherDetails?.[0]?.TypeOfSales ?? 1,
          ArticleUnitQty:
            (partial.TypeOfSales ?? prev.ArticleOtherDetails?.[0]?.TypeOfSales ?? 1) === 2
              ? (partial.ArticleUnitQty ?? prev.ArticleOtherDetails?.[0]?.ArticleUnitQty ?? 1)
              : 1,
          NewSalesPrice: partial.NewSalesPrice ?? prev.SalesPrice,
          IsActive: true,
        },
      ],
    }));
  };

  const addAdvancedRow = () => {
    setFormData((prev) => ({
      ...prev,
      ArticleOtherDetails: [
        ...(prev.ArticleOtherDetails || []),
        {
          NewBarcode: "",
          ArticleUnitID: prev.UnitID || "",
          TypeOfSales: 1,
          ArticleUnitQty: 1,
          NewSalesPrice: prev.SalesPrice || 0,
          IsActive: true,
        },
      ],
    }));
  };

  const updateAdvancedRow = (index, key, value) => {
    const errorKeyMap = {
      TypeOfSales: "AdvancedType",
      ArticleUnitID: "AdvancedUnit",
      ArticleUnitQty: "AdvancedQty",
      NewBarcode: "AdvancedBarcode",
      NewSalesPrice: "AdvancedPrice",
    };

    setFormData((prev) => ({
      ...prev,
      ArticleOtherDetails: (prev.ArticleOtherDetails || []).map((row, rowIndex) => {
        if (rowIndex !== index) return row;

        if (key === "TypeOfSales") {
          const typeValue = Number(value) || 1;
          return {
            ...row,
            TypeOfSales: typeValue,
            ArticleUnitQty: typeValue === 2 ? (row.ArticleUnitQty || 1) : 1,
          };
        }

        if (key === "ArticleUnitQty") {
          return {
            ...row,
            ArticleUnitQty: value,
          };
        }

        return { ...row, [key]: value };
      }),
    }));
    setErrors((prev) => ({ ...prev, [`${errorKeyMap[key] || key}_${index}`]: "" }));
  };

  const removeAdvancedRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      ArticleOtherDetails: (prev.ArticleOtherDetails || []).filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleGroupSave = async (payload) => {
    try {
      await createArticleGroup(payload);
      await loadDependencies();
      handleSuccess("Grupi u ruajt me sukses.");
      setIsGroupModalOpen(false);
    } catch (error) {
      errorAlert(error?.message || "Gabim gjatë ruajtjes së grupit.");
    }
  };

  const handleUnitSave = async (payload) => {
    try {
      await createUnit(payload);
      await loadDependencies();
      handleSuccess("Njësia u ruajt me sukses.");
      setIsUnitModalOpen(false);
    } catch (error) {
      errorAlert(error?.message || "Gabim gjatë ruajtjes së njësisë.");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isReadOnly) return;
    setSaving(true);

    try {
      const isValid = await validate();
      if (!isValid) return;

      const payload = {
        Barcode: formData.Barcode.trim(),
        ArticleName: formData.ArticleName.trim(),
        Description: formData.Description || "",
        ArticleProduction: Number(formData.ArticleProduction),
        GroupID: Number(formData.GroupID),
        UnitID: Number(formData.UnitID),
        LedgerID: Number(formData.LedgerID),
        VAT: Number(formData.VAT),
        PurchasePrice: 0,
        SalesPrice: toNumberOrZero(formData.SalesPrice),
        IsActive: Boolean(formData.IsActive),
        TouchScreen: Boolean(formData.TouchScreen),
        ArticleOtherDetails: (formData.ArticleOtherDetails || []).map((d) => ({
          ArticleSalesOptionsID: d.ArticleSalesOptionsID || null,
          NewBarcode: d.NewBarcode || formData.Barcode,
          ArticleUnitID: Number(d.ArticleUnitID) || Number(formData.UnitID),
          TypeOfSales: Number(d.TypeOfSales) || 1,
          ArticleUnitQty: Number(d.TypeOfSales) === 2 ? (parseFloat(d.ArticleUnitQty) || 1) : 1,
          NewSalesPrice: toNumberOrZero(d.NewSalesPrice),
          IsActive: Boolean(d.IsActive ?? true),
        })),
      };

      if (isEditMode && currentArticleId) {
        await updateCompoundArticle(currentArticleId, payload);
        handleSuccess("Artikulli i përbërë u përditësua me sukses.");
      } else {
        await createCompoundArticle(payload);
        handleSuccess("Artikulli i përbërë u ruajt me sukses.");
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      errorAlert(getCompoundArticleErrorMessage(err, "Gabim gjatë ruajtjes së artikullit."));
    } finally {
      setSaving(false);
    }
  };

  const groupValue = groups.find((g) => Number(g.GroupID) === Number(formData.GroupID)) || null;
  const unitValue = units.find((u) => Number(u.UnitID) === Number(formData.UnitID)) || null;
  const ledgerValue = ledgers.find((l) => Number(l.LedgerID) === Number(formData.LedgerID)) || null;
  const vatValue = vatRates.find((v) => Number(v.VATID) === Number(formData.VAT)) || null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle className="compound-modal__title-bar">
        <div>
          <div className="compound-modal__title">
            {isReadOnly
              ? "Shfaq artikullin e përbërë"
              : isEditMode
                ? "Përditëso artikullin e përbërë"
                : "Krijo artikull të përbërë"}
          </div>
          <div className="compound-modal__subtitle">
            {isReadOnly
              ? "Pamje vetëm për lexim me të gjitha të dhënat e artikullit."
              : "Formë e organizuar për insertim dhe editim me validim të plotë."}
          </div>
        </div>
      </DialogTitle>

      <DialogContent className="compound-modal__body">
        {loading ? (
          <div className="compound-modal__loading">
            <CircularProgress size={24} />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="compound-modal__form">
            <div className="compound-modal__grid two-cols">
              <TextField
                required
                label="Barkodi"
                value={formData.Barcode}
                disabled={isReadOnly}
                onChange={(e) => {
                  setField("Barcode", e.target.value);
                  syncDefaultAdvancedRow({ NewBarcode: e.target.value });
                }}
                onBlur={(e) => validateField("Barcode", e.target.value?.trim())}
                error={Boolean(errors.Barcode)}
                helperText={errors.Barcode}
              />
              <TextField
                required
                label="Emri i artikullit"
                value={formData.ArticleName}
                disabled={isReadOnly}
                onChange={(e) => {
                  setField("ArticleName", e.target.value);
                  if (!isEditMode && !formData.Description) {
                    setField("Description", e.target.value);
                  }
                }}
                onBlur={(e) => validateField("ArticleName", e.target.value?.trim())}
                error={Boolean(errors.ArticleName)}
                helperText={errors.ArticleName}
              />
            </div>

            <div className="compound-modal__grid two-cols">
              <TextField
                multiline
                minRows={3}
                label="Përshkrimi"
                value={formData.Description}
                disabled={isReadOnly}
                onChange={(e) => setField("Description", e.target.value)}
              />
              <TextField
                select
                required
                label="Lloji i artikullit"
                value={formData.ArticleProduction}
                disabled={isReadOnly}
                onChange={(e) => setField("ArticleProduction", e.target.value)}
                error={Boolean(errors.ArticleProduction)}
                helperText={errors.ArticleProduction}
              >
                {articleProduction.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.type}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div className="compound-modal__grid two-cols">
              <div>
                <label className="compound-modal__label">Grupi</label>
                <div className="compound-modal__select-with-button">
                  <Select
                    styles={selectStyles}
                    hasError={Boolean(errors.GroupID)}
                    isDisabled={isReadOnly}
                    value={
                      groupValue
                        ? { value: groupValue.GroupID, label: groupValue.GroupName }
                        : null
                    }
                    options={groups.map((g) => ({ value: g.GroupID, label: g.GroupName }))}
                    onChange={(opt) => {
                      setField("GroupID", opt ? opt.value : "");
                      validateField("GroupID", opt ? opt.value : "");
                    }}
                    onBlur={() => validateField("GroupID", formData.GroupID)}
                    isClearable
                    placeholder="Zgjedh grupin"
                  />
                  {!isReadOnly ? (
                    <button
                      type="button"
                      className="compound-modal__add-btn"
                      onClick={() => setIsGroupModalOpen(true)}
                    >
                      <Plus size={18} />
                    </button>
                  ) : null}
                </div>
                {errors.GroupID ? <span className="compound-modal__error">{errors.GroupID}</span> : null}
              </div>

              <div>
                <label className="compound-modal__label">Njësia matëse</label>
                <div className="compound-modal__select-with-button">
                  <Select
                    styles={selectStyles}
                    hasError={Boolean(errors.UnitID)}
                    isDisabled={isReadOnly}
                    value={
                      unitValue
                        ? { value: unitValue.UnitID, label: unitValue.UnitName }
                        : null
                    }
                    options={units.map((u) => ({ value: u.UnitID, label: u.UnitName }))}
                    onChange={(opt) => {
                      setField("UnitID", opt ? opt.value : "");
                      syncDefaultAdvancedRow({ ArticleUnitID: opt ? opt.value : "" });
                      validateField("UnitID", opt ? opt.value : "");
                    }}
                    onBlur={() => validateField("UnitID", formData.UnitID)}
                    isClearable
                    placeholder="Zgjedh njësinë"
                  />
                  {!isReadOnly ? (
                    <button
                      type="button"
                      className="compound-modal__add-btn"
                      onClick={() => setIsUnitModalOpen(true)}
                    >
                      <Plus size={18} />
                    </button>
                  ) : null}
                </div>
                {errors.UnitID ? <span className="compound-modal__error">{errors.UnitID}</span> : null}
              </div>
            </div>

            <div className="compound-modal__grid two-cols">
              <div>
                <label className="compound-modal__label">Llogaria kontabël</label>
                <Select
                  styles={selectStyles}
                  hasError={Boolean(errors.LedgerID)}
                  isDisabled={isReadOnly}
                  value={
                    ledgerValue
                      ? { value: ledgerValue.LedgerID, label: ledgerValue.LedgerNameEN }
                      : null
                  }
                  options={ledgers.map((l) => ({ value: l.LedgerID, label: l.LedgerNameEN }))}
                  onChange={(opt) => {
                    setField("LedgerID", opt ? opt.value : "");
                    validateField("LedgerID", opt ? opt.value : "");
                  }}
                  onBlur={() => validateField("LedgerID", formData.LedgerID)}
                  isClearable
                  placeholder="Zgjedh llogarinë"
                />
                {errors.LedgerID ? <span className="compound-modal__error">{errors.LedgerID}</span> : null}
              </div>

              <div>
                <label className="compound-modal__label">Norma e TVSH-së</label>
                <Select
                  styles={selectStyles}
                  hasError={Boolean(errors.VAT)}
                  isDisabled={isReadOnly}
                  value={
                    vatValue
                      ? { value: vatValue.VATID, label: `${vatValue.VATName} (${vatValue.VATPercent}%)` }
                      : null
                  }
                  options={vatRates.map((v) => ({
                    value: v.VATID,
                    label: `${v.VATName} (${v.VATPercent}%)`,
                  }))}
                  onChange={(opt) => {
                    setField("VAT", opt ? opt.value : "");
                    validateField("VAT", opt ? opt.value : "");
                  }}
                  onBlur={() => validateField("VAT", formData.VAT)}
                  isClearable
                  placeholder="Zgjedh TVSH-në"
                />
                {errors.VAT ? <span className="compound-modal__error">{errors.VAT}</span> : null}
              </div>
            </div>

            <div className="compound-modal__grid two-cols">
              <PriceField
                label="Çmimi i shitjes pa TVSH"
                name="salesNoVat"
                value={salesNoVat}
                disabled={isReadOnly}
                onChange={(e) => handleSalesNoVatChange(e.target.value)}
                decimalScale={2}
              />
              <div>
                <PriceField
                  label="Çmimi i shitjes me TVSH"
                  name="SalesPrice"
                  value={formData.SalesPrice}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    setField("SalesPrice", e.target.value);
                    syncDefaultAdvancedRow({ NewSalesPrice: e.target.value });
                  }}
                  decimalScale={2}
                />
                {errors.SalesPrice ? <span className="compound-modal__error">{errors.SalesPrice}</span> : null}
              </div>
            </div>

            <div className="compound-modal__advanced-toggle">
              <FormControlLabel
                control={
                  <Switch
                    checked={showAdvancedForm}
                    disabled={!formData.ArticleOtherDetails?.length}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowAdvancedForm(checked);
                      if (!checked) {
                        syncDefaultAdvancedRow({});
                      }
                    }}
                  />
                }
                label="Shfaq opsionet shtesë"
              />
              {errors.Advanced ? <span className="compound-modal__error">{errors.Advanced}</span> : null}
            </div>

            {showAdvancedForm ? (
              <div className="compound-modal__advanced-panel">
                <div className="compound-modal__advanced-header">
                  <span>Opsionet shtesë të shitjes</span>
                  {!isReadOnly ? (
                    <Button size="small" variant="outlined" onClick={addAdvancedRow}>
                      Shto Rresht
                    </Button>
                  ) : null}
                </div>

                <div className="compound-modal__advanced-table-wrapper">
                  <TableContainer component={Paper} className="compound-modal__table-container">
                    <Table size="small">
                      <TableHead className="compound-modal__table-head">
                        <TableRow>
                          <TableCell>Barkodi</TableCell>
                          <TableCell>Lloji shitjes</TableCell>
                          <TableCell>Njësia artikullit</TableCell>
                          <TableCell>Sasia Shumices</TableCell>
                          <TableCell>Cmimi</TableCell>
                          <TableCell>Aktiv</TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(formData.ArticleOtherDetails || []).map((row, index) => (
                          <TableRow key={`advanced-${index}`}>
                            <TableCell>
                              <TextField
                                label="Barkodi"
                                variant="outlined"
                                value={row.NewBarcode || ""}
                                disabled={isReadOnly}
                                onChange={(e) => updateAdvancedRow(index, "NewBarcode", e.target.value)}
                                error={Boolean(errors[`AdvancedBarcode_${index}`])}
                                helperText={errors[`AdvancedBarcode_${index}`] || ""}
                                sx={{ width: "200px" }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                select
                                label="Shitje pakicë/shumicë"
                                value={row.TypeOfSales || 1}
                                disabled={isReadOnly}
                                onChange={(e) => updateAdvancedRow(index, "TypeOfSales", e.target.value)}
                                error={Boolean(errors[`AdvancedType_${index}`])}
                                helperText={errors[`AdvancedType_${index}`] || ""}
                                sx={{ width: "200px" }}
                              >
                                {saleTypes.map((saleType) => (
                                  <MenuItem key={`sale-type-${saleType.id}`} value={saleType.id}>
                                    {saleType.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField
                                select
                                label="Njesia e artikullit"
                                value={row.ArticleUnitID || ""}
                                disabled={isReadOnly}
                                onChange={(e) => updateAdvancedRow(index, "ArticleUnitID", e.target.value)}
                                error={Boolean(errors[`AdvancedUnit_${index}`])}
                                helperText={errors[`AdvancedUnit_${index}`] || ""}
                                sx={{ width: "200px" }}
                              >
                                {units.map((unit) => (
                                  <MenuItem key={`unit-${unit.UnitID}`} value={unit.UnitID}>
                                    {unit.UnitName}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number"
                                label="Sasia Shumices"
                                variant="outlined"
                                value={row.ArticleUnitQty || ""}
                                disabled={isReadOnly || Number(row.TypeOfSales) !== 2}
                                inputProps={{ min: 1, step: "0.01" }}
                                onChange={(e) => updateAdvancedRow(index, "ArticleUnitQty", e.target.value)}
                                error={Boolean(errors[`AdvancedQty_${index}`])}
                                helperText={
                                  Number(row.TypeOfSales) === 1
                                    ? "Për pakicë vendoset 1"
                                    : errors[`AdvancedQty_${index}`] || ""
                                }
                                sx={{ width: "200px" }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                label="Cmimi shitjes"
                                variant="outlined"
                                value={row.NewSalesPrice || ""}
                                disabled={isReadOnly}
                                onChange={(e) => updateAdvancedRow(index, "NewSalesPrice", e.target.value)}
                                error={Boolean(errors[`AdvancedPrice_${index}`])}
                                helperText={errors[`AdvancedPrice_${index}`] || ""}
                                sx={{ width: "200px" }}
                              />
                            </TableCell>
                            <TableCell>
                              <FormControlLabel
                                className="compound-modal__active-toggle"
                                control={
                                  <Switch
                                    checked={Boolean(row.IsActive)}
                                    disabled={isReadOnly}
                                    onChange={(e) => updateAdvancedRow(index, "IsActive", e.target.checked)}
                                  />
                                }
                                label="Aktiv"
                              />
                            </TableCell>
                            <TableCell>
                              {!isReadOnly ? (
                                <Button
                                  type="button"
                                  color="error"
                                  variant="text"
                                  onClick={() => removeAdvancedRow(index)}
                                  disabled={(formData.ArticleOtherDetails || []).length === 1}
                                  className="compound-modal__remove-btn"
                                >
                                  <Trash2 size={16} />
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </div>
            ) : null}

            <div className="compound-modal__switches">
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.IsActive)}
                    disabled={isReadOnly}
                    onChange={(e) => setField("IsActive", e.target.checked)}
                  />
                }
                label="Artikull aktiv"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.TouchScreen)}
                    disabled={isReadOnly}
                    onChange={(e) => setField("TouchScreen", e.target.checked)}
                  />
                }
                label="Shfaq në POS"
              />
            </div>

            <div className="compound-modal__actions">
              <Button variant="outlined" onClick={handleClose} disabled={saving}>
                {isReadOnly ? "Mbyll pamjen" : "Mbyll"}
              </Button>
              {!isReadOnly ? (
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || !isDirty}
                >
                  {saving ? "Duke ruajtur..." : isEditMode ? "Përditëso" : "Ruaj"}
                </Button>
              ) : null}
            </div>
          </form>
        )}
      </DialogContent>

      <ArticleGroupModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        onSave={handleGroupSave}
      />

      <UnitModal
        isOpen={isUnitModalOpen}
        onClose={() => setIsUnitModalOpen(false)}
        onSave={handleUnitSave}
      />

      <Dialog open={showCloseConfirm} onClose={() => setShowCloseConfirm(false)} maxWidth="xs">
        <DialogTitle>Të dhënat nuk janë ruajtur</DialogTitle>
        <DialogContent>
          Ke ndryshime të paruajtura. Nëse mbyll, të dhënat humbin. Dëshiron të vazhdosh?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseConfirm(false)}>Kthehu</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setShowCloseConfirm(false);
              setIsDirty(false);
              onClose();
            }}
          >
            Mbyll pa ruajtur
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default function CompoundArticleForm(props) {
  const [modalOpen, setModalOpen] = useState(false);
  const isControlledOpen = typeof props.show === "boolean";
  const openState = isControlledOpen ? props.show : modalOpen;

  const closeModal = () => {
    if (isControlledOpen && props.onHide) {
      props.onHide();
      return;
    }
    setModalOpen(false);
  };

  const handleSuccess = () => {
    if (props.refreshData) props.refreshData();
    if (props.fetchData) props.fetchData();
  };

  return (
    <div>
      {props.type === "text" ? (
        <AddElementsButton
          text="Krijo Artikull të përbërë"
          onClick={() => setModalOpen(true)}
          icon="bi bi-upc-scan"
          className="button-68"
        />
      ) : props.type === "insert" ? (
        <AddElementsButton
          text="Krijo Artikull të përbërë"
          onClick={() => setModalOpen(true)}
          icon="bi bi-upc-scan"
          className="button-15"
        />
      ) : props.type === "menu" ? (
        <i className="bi bi-upc-scan" onClick={() => setModalOpen(true)}></i>
      ) : (
        <AddBoxIcon style={{ height: "29px", color: "black" }} onClick={() => setModalOpen(true)} />
      )}

      <CompoundArticleModal
        open={openState}
        onClose={closeModal}
        onSuccess={handleSuccess}
        itemToEdit={props.itemToEdit}
      />
    </div>
  );
}
